/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsCascadePicker = require('./PictProvider-InputType-MaterialsCascadePicker.js');
const libLabRouting = require('./_LabRouting.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'MaterialsTestLabPicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	Entity: 'Lab',
};

/**
 * The per-test Lab picker (beast B4): the Picker-stack port of MaterialsTestLabSelector, mirroring the
 * legacy provider 1:1 on the MaterialsCascadePicker base.
 *
 * SIBLING of MaterialsLabPicker (NOT a subclass): both are thin layers with Entity:'Lab', but the BIND
 * TARGET differs — MaterialsLabPicker binds the sample's labs (SampleLabJoin); this one binds a single
 * TEST's lab (the row's TestInstance from the Test selector's triple). On a lab pick it DUAL-WRITES the
 * test's lab — `TestInstance.IDLab` (direct FK) and a `TestInstanceLabJoin` — and asks the sample's lab
 * selector to reconcile with the per-test labs (`SampleLabSync`, see _LabRouting). Single-select (one lab
 * per test).
 */
class PictProviderInputTypeMaterialsTestLabPicker extends MaterialsCascadePicker
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict & { TransactionTracking: any, TempData: any }} */
		this.pict;
	}

	/** Labs are customer-scoped (row-level auth), sorted by Name — same as the sample lab picker.
	 *  @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] @param {Object} [pView]
	 *  @return {{Filters: Array<string>, BackOffFilters: Array<string>}} */
	getContextualSearchFilters(pInput, pRowIndex, pView)
	{
		const tmpSplit = super.getContextualSearchFilters(pInput, pRowIndex, pView);
		tmpSplit.Filters.push('FSF~Name~ASC~0');
		return tmpSplit;
	}

	/** Init: legacy defaults (single-select, Name EntityTag). */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._applyTestLabDefaults(pInput);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Tabular init + the persisted-lab seed: the column binds to the nested TestInstance.IDLab, which a
	 * tabular cell does NOT marshal into pValue on (re)open — so a persisted/adopted lab renders blank.
	 * PUSH it via the value-commit primitive — which writes the value AND drives the fetch + widget
	 * paint, exactly like a fresh pick. Ported 1:1.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._applyTestLabDefaults(pInput);
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if ((pValue === undefined || pValue === null || pValue === '') && pRowIndex != null)
		{
			const tmpTestInstance = this._getRowTestInstance(pView, pInput, pRowIndex);
			if (tmpTestInstance && tmpTestInstance.IDLab)
			{
				this._setValueAndCommit(pView, pInput, tmpTestInstance.IDLab, pRowIndex);
			}
			else
			{
				this._autoFillTestLab(pView, pInput, pRowIndex);
			}
		}
		return tmpResult;
	}

	/**
	 * Fill a row with the resolver's auto-fill answer (see LabScopeResolver.getAutoFillLabID). Only ever runs
	 * on a row that has no lab, so a persisted or user-chosen value is never overwritten; commits through the
	 * same primitive a user pick uses, so the join dual-write and the sample-lab sync ride along unchanged.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string} pRowIndex
	 */
	_autoFillTestLab(pView, pInput, pRowIndex)
	{
		libLabRouting.autoFillTestLab(this.pict, pInput, pRowIndex, pView,
			() => this._getRowTestInstance(pView, pInput, pRowIndex)?.IDLab,
			(pIDLab) => this._setValueAndCommit(pView, pInput, pIDLab, pRowIndex));
	}

	/** One lab per test (single-select), tag on Name. @param {Record<string, any>} pInput */
	_applyTestLabDefaults(pInput)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.Multiple == null)
		{
			pInput.PictForm.Multiple = false;
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Name';
		}
	}

	/**
	 * The TestLab cascade — the port of the legacy `_onRecordRefresh`: dual-write the test's lab
	 * (TestInstance.IDLab + a TestInstanceLabJoin), ask the sample's labs to reconcile with the per-test
	 * labs, then the in-section repaint + the family bus (super). The row record IS the test triple.
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {Record<string, any>|Array<Record<string, any>>} pRecord
	 * @param {number|string} [pRowIndex] @param {string} [pTransactionGUID]
	 */
	_onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID)
	{
		const tmpLab = Array.isArray(pRecord) ? pRecord[0] : pRecord;
		const tmpIDLab = tmpLab && tmpLab.IDLab;
		const tmpTestInstance = this._getRowTestInstance(pView, pInput, pRowIndex);
		if (tmpTestInstance && tmpIDLab)
		{
			tmpTestInstance.IDLab = tmpIDLab;
			this._ensureTestInstanceLabJoin(pView, pInput, pRowIndex, tmpTestInstance, tmpLab);
		}
		libLabRouting.requestSampleLabSync(this.pict);
		// ensures any fields that use this record are updated immediately, if they are in the same section
		if (typeof pView.marshalToView === 'function')
		{
			pView.marshalToView();
		}
		super._onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID);
	}

	/**
	 * The TestInstance for this row. The Testing-G2 table binds to the Test selector's triple collection
	 * (`${TestAddress}_Instances`), so the row record is `{ TestInstance, TestInstanceSampleJoin, ... }`.
	 * Ported 1:1.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @return {Record<string, any>|null}
	 */
	_getRowTestInstance(pView, pInput, pRowIndex)
	{
		const tmpRowAddress = this._getRecordAddress(pInput, pRowIndex);
		// the lab address sits inside the row record; walk up to the row triple and read its TestInstance
		const tmpTripleAddress = pInput.PictForm.TestInstanceTripleAddress || tmpRowAddress.replace(/\.[^.]+$/, '');
		const tmpTriple = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTripleAddress);
		if (tmpTriple && tmpTriple.TestInstance && typeof tmpTriple.TestInstance === 'object')
		{
			return tmpTriple.TestInstance;
		}
		// fallback: the row record itself is/holds the TestInstance
		return (tmpTriple && typeof tmpTriple === 'object' && !Array.isArray(tmpTriple)) ? tmpTriple : null;
	}

	/**
	 * Create (or retarget) the TestInstanceLabJoin for this test instance, stored on the row triple at
	 * `_TestInstanceLabJoin` so the projection can sweep it into _EntityData. Ported 1:1.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {Record<string, any>} pTestInstance @param {Record<string, any>} pLab
	 */
	_ensureTestInstanceLabJoin(pView, pInput, pRowIndex, pTestInstance, pLab)
	{
		// Compose the row-triple sibling address (…_Instances[row]._TestInstanceLabJoin) directly. The record
		// address is the lab's own (…row.TestInstance.IDLab__Record), so stripping one segment lands INSIDE the
		// TestInstance — which drags the join into the TestInstance insert and breaks it with malformed SQL.
		const tmpJoinAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, '_TestInstanceLabJoin');
		const tmpExisting = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinAddress);
		const tmpJoin = (tmpExisting && typeof tmpExisting === 'object' && !Array.isArray(tmpExisting)) ? tmpExisting : { };
		tmpJoin.IDLab = pLab.IDLab;
		if (pTestInstance.IDTestInstance > 0)
		{
			tmpJoin.IDTestInstance = pTestInstance.IDTestInstance;
			delete tmpJoin.GUIDTestInstance;
		}
		else if (pTestInstance.GUIDTestInstance)
		{
			tmpJoin.GUIDTestInstance = pTestInstance.GUIDTestInstance;
			delete tmpJoin.IDTestInstance;
		}
		if (!tmpJoin.GUIDTestInstanceLabJoin)
		{
			const tmpDocumentGUID = this.pict.manifest.getValueAtAddress(this.pict, 'AppData.DocumentData.ReportData.GUIDDocument') || '';
			// Keyed off the test instance rather than a fresh UUID. There is one join per instance, so the
			// key has to be re-derivable - a re-mint that cannot reproduce it inserts a duplicate row
			// instead of upserting the existing one.
			let tmpTestKey = `ID${pTestInstance.IDTestInstance}`;
			if (pTestInstance.GUIDTestInstance)
			{
				tmpTestKey = String(pTestInstance.GUIDTestInstance).replace(`${tmpDocumentGUID}-`, '');
			}
			tmpJoin.GUIDTestInstanceLabJoin = `${tmpDocumentGUID}-TILJ-${tmpTestKey}`;
		}
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinAddress, tmpJoin);
	}

	/**
	 * Per-row save-time projection hook, dispatched by the base _handleEvent on the sweep. A lab picker
	 * creates no Lab reference record, so the base's creation-gated `__Record` projection is replaced
	 * with projecting this row's TestInstanceLabJoin (rows with no lab picked staged no join, so the
	 * sync no-ops on them). Ported 1:1.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleBaseSaveProjectionEvent(pView, pInput, pRowIndex, pEvent, pTransactionGUID)
	{
		if (typeof pEvent !== 'string')
		{
			return;
		}
		const tmpTarget = this._getProjectionTarget(pInput, pEvent);
		if (!tmpTarget)
		{
			return;
		}
		this._syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
	}

	/**
	 * Save-time projection: sweep this row's TestInstanceLabJoin into _EntityData. (TestInstance.IDLab
	 * itself rides the Test selector's TestInstance projection — we only own the join here.) Ported 1:1.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {string} pTransactionGUID @param {Object} pTargetObject
	 */
	_syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}
		// Same composed row-triple sibling address as _ensureTestInstanceLabJoin (see the note there).
		const tmpJoinAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, '_TestInstanceLabJoin');
		const tmpJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinAddress);
		if (tmpJoin && typeof tmpJoin === 'object' && !Array.isArray(tmpJoin) && tmpJoin.GUIDTestInstanceLabJoin)
		{
			if (!tmpEntityData.TestInstanceLabJoin || typeof tmpEntityData.TestInstanceLabJoin !== 'object' || Array.isArray(tmpEntityData.TestInstanceLabJoin))
			{
				tmpEntityData.TestInstanceLabJoin = { };
			}
			tmpEntityData.TestInstanceLabJoin[tmpJoin.GUIDTestInstanceLabJoin] = tmpJoin;
		}
	}
}

module.exports = PictProviderInputTypeMaterialsTestLabPicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
